import {
  beginSessionExpiryTransition,
  isSessionExpiryTransitionStarted,
  SessionExpiredError,
} from './sessionExpiry';

export interface SessionTokenSource {
  getSessionJwt: () => string | undefined;
  refreshSession: () => Promise<void>;
}

/**
 * The refresh in flight, shared by every request that 401s while it runs.
 *
 * Tagged with the `refreshSession` that started it, and joined only by requests
 * whose captured source carries that same function. A request captures its
 * source when it starts, so one that began under an old source and 401s after
 * the source changed must not join the new source's refresh (it would replay
 * with a token from a session it never held), nor the other way round.
 *
 * The tag is stable for the life of a Stytch client: `B2BSdkBridge` memoizes
 * the token functions on the client alone, so session and profile changes do
 * not rotate it. Only the `getSessionJwt() === attemptedJwt` guard in
 * `fetchWithSession` decides whether a refresh is needed at all; this only
 * collapses concurrent ones.
 */
let activeRefresh: {
  owner: SessionTokenSource['refreshSession'];
  promise: Promise<void>;
} | null = null;

function refreshOnce(session: SessionTokenSource): Promise<void> {
  const owner = session.refreshSession;
  if (activeRefresh?.owner === owner) return activeRefresh.promise;

  const entry = {
    owner,
    promise: Promise.resolve()
      .then(owner)
      .finally(() => {
        // Identity check: a reset or another source's refresh may have
        // replaced the slot meanwhile. Clearing our own entry is required — a
        // retained rejected promise would turn every later 401 into an
        // instant terminal expiry.
        if (activeRefresh === entry) activeRefresh = null;
      }),
  };
  activeRefresh = entry;
  return entry.promise;
}

/**
 * Forget any in-flight refresh. The transport calls this when its session
 * source changes, so a new provider's requests never join the old one's
 * refresh; tests call it because module state outlives a test file.
 */
export function resetActiveRefresh(): void {
  activeRefresh = null;
}

/** Fetch once with current JWT, then refresh and replay exactly once on 401. */
export async function fetchWithSession(
  input: RequestInfo | URL,
  init: RequestInit = {},
  session: SessionTokenSource,
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  // TanStack or caller-level retries can re-enter this transport after terminal
  // expiry. Once logout/navigation starts, fail locally instead of issuing a
  // second refresh/replay sequence.
  if (isSessionExpiryTransitionStarted()) throw new SessionExpiredError();

  const requestInput =
    typeof input === 'string'
      ? new URL(
          input,
          globalThis.location?.origin && globalThis.location.origin !== 'null'
            ? globalThis.location.origin
            : 'http://localhost',
        )
      : input;

  // Keep one untouched Request so every attempt gets a fresh clone. This
  // preserves FormData, Blob, and stream bodies without reusing a consumed
  // request after a 401.
  const template = new Request(requestInput, {
    ...init,
    cache: init.cache ?? 'no-store',
    credentials: init.credentials ?? 'include',
  });

  const request = (sessionJwt: string | undefined) => {
    const headers = new Headers(template.headers);
    // Authorization always belongs to current session state, never caller
    // input or the previous attempt.
    headers.delete('Authorization');
    if (sessionJwt) headers.set('Authorization', `Bearer ${sessionJwt}`);

    return fetchImpl(
      new Request(template.clone(), {
        headers,
      }),
    );
  };

  const attemptedJwt = session.getSessionJwt();
  let response = await request(attemptedJwt);
  if (response.status !== 401 || !attemptedJwt) return response;

  // A caller's account guard can cancel an obsolete request. That does not
  // mean the newly signed-in account has expired.
  const currentJwt = session.getSessionJwt();
  try {
    // Another request may already have refreshed this exact stale token while
    // this response was in flight. In that case, replay with current state
    // without performing a redundant refresh.
    if (currentJwt === attemptedJwt) {
      await refreshOnce(session);
    }
  } catch {
    beginSessionExpiryTransition();
    throw new SessionExpiredError();
  }

  response = await request(session.getSessionJwt());
  if (response.status === 401) {
    beginSessionExpiryTransition();
    throw new SessionExpiredError();
  }
  return response;
}
